/**
 * Dedupe CableReady "update" fetches. The same broadcast can trigger multiple
 * cable-ready-updates-for elements to call update(); each does a fetch with
 * X-Cable-Ready: update. Both requests share the same call stack and hit the
 * same URL. We wrap fetch so the second identical request within a short window
 * reuses the first response (clone), avoiding a duplicate network round-trip.
 * Must be loaded before CableReady.initialize().
 */

const CABLE_READY_UPDATE_HEADER = "x-cable-ready";
const COALESCE_MS = 200;

/** Cached response for same-URL coalesce after a fetch completes */
const cache = new Map();
/** In-flight fetch promise per URL so a second request reuses the first (no parallel fetches) */
const inFlight = new Map();

function normalizeKey(url) {
  try {
    const u = new URL(url, window.location.origin);
    u.searchParams.delete("_debug_refresh_source");
    const search = normalizedSearch(u.searchParams);
    return u.origin + u.pathname + (search ? `?${search}` : "");
  } catch {
    return String(url);
  }
}

function isCableReadyUpdateRequest(input, init) {
  if (input instanceof Request) {
    return input.headers.get(CABLE_READY_UPDATE_HEADER) === "update";
  }
  const headers = init?.headers;
  if (headers instanceof Headers) return headers.get(CABLE_READY_UPDATE_HEADER) === "update";
  if (headers && typeof headers === "object" && !Array.isArray(headers)) {
    const v = headers[CABLE_READY_UPDATE_HEADER] ?? headers["X-Cable-Ready"];
    return v === "update";
  }
  return false;
}

function resolveCableReadyFetchUrl(input, init) {
  const url = typeof input === "string" ? input : input?.url;
  if (!url) return { url, input }

  try {
    const requested = new URL(url, window.location.origin);
    const current = new URL(window.location.href, window.location.origin);

    if (
      requested.origin === current.origin &&
      requested.pathname === current.pathname &&
      current.search &&
      !requested.search
    ) {
      const resolvedUrl = current.href;

      if (input instanceof Request) {
        return { url: resolvedUrl, input: new Request(resolvedUrl, input) }
      }

      return { url: resolvedUrl, input: resolvedUrl }
    }
  } catch {
    // Fall through and use the original request.
  }

  return { url, input }
}

const CR_FETCH_LOG_PREFIX = "[refresh:cable_ready_fetch]";

const realFetch = window.fetch;
window.fetch = function (input, init) {
  if (!isCableReadyUpdateRequest(input, init)) {
    return realFetch.apply(this, arguments);
  }
  const resolved = resolveCableReadyFetchUrl(input, init);
  const url = resolved.url;
  if (!url) {
    return realFetch.apply(this, arguments);
  }
  const key = normalizeKey(url);
  const now = Date.now();
  const entry = cache.get(key);
  if (entry && now - entry.at < COALESCE_MS) {
    console.log(CR_FETCH_LOG_PREFIX, "dedupe: return cached response (no network)", {
      url: key,
      msSinceFirstFetch: now - entry.at,
    });
    return Promise.resolve(entry.response.clone());
  }
  const pending = inFlight.get(key);
  if (pending) {
    console.log(CR_FETCH_LOG_PREFIX, "dedupe: wait for in-flight request (no extra network)", { url: key });
    return pending.then((response) => (response.ok ? response.clone() : response));
  }
  console.log(CR_FETCH_LOG_PREFIX, "fetch (CableReady update morph)", { url: key });
  const promise = realFetch
    .call(this, resolved.input, init)
    .then((response) => {
      inFlight.delete(key);
      if (!response.ok) {
        console.log(CR_FETCH_LOG_PREFIX, "fetch failed (not cached)", { url: key, ok: response.ok });
        return response;
      }
      const toCache = response.clone();
      cache.set(key, { response: toCache, at: Date.now() });
      setTimeout(() => cache.delete(key), COALESCE_MS + 50);
      return response;
    })
    .catch((err) => {
      inFlight.delete(key);
      throw err;
    });
  inFlight.set(key, promise);
  return promise;
};

function normalizedSearch(searchParams) {
  const entries = Array.from(searchParams.entries()).sort(([leftKey, leftValue], [rightKey, rightValue]) => {
    if (leftKey === rightKey) return leftValue.localeCompare(rightValue);
    return leftKey.localeCompare(rightKey);
  });

  return new URLSearchParams(entries).toString();
}
